import type { Action } from './Action';
import type { PermissionsSnapshot, PermissionsSource } from './PermissionsSnapshot';
import type { ResourceFacts } from './ResourceFacts';

export type PermissionDecision = {
  allowed: boolean;
  /** No answer yet. `allowed` is false, but a gate should hold rather than deny. */
  isLoading: boolean;
  /** The answer could not be determined, so it denied. A determined denial has this false. */
  isError: boolean;
};

/**
 * How an action reads `ResourceFacts`. Deliberately a per-action table rather than one blanket
 * rule, because the rules genuinely differ - see `FACT_RULES`.
 */
type FactRule =
  /** Facts are irrelevant; the snapshot is the whole answer. */
  | 'ignore'
  /**
   * Owner-or-host grants on its own, otherwise the snapshot grants but only for a resource the
   * snapshot's scope actually covers.
   */
  | 'scopedOrIsOwned'
  /** The snapshot grants only in combination with owner-or-host. Both are required. */
  | 'andIsOwned';

/**
 * Mirrors what the app enforces today, which in turn mirrors the Cedar policies that gate the
 * writes server-side. Reproducing this faithfully is the point: the layer must not change who can
 * do what (HNS-323).
 *
 * - `natter:manage` follows `natter-manage-admin-owner-or-host`: a community admin manages any
 *   event in that community, and an owner or host manages their own. No community-wide privilege is
 *   ANDed in, because none is required server-side for an owner or host (natter-backend ADR-0010).
 *   The admin branch is scoped to the event's community, matching Cedar's
 *   `resource in principal.communityAdminOf`, so an admin of one community is not treated as an
 *   admin of every event they can fetch.
 * - `report:view` and `report:manage` keep their privilege preconditions: they resolve against the
 *   report's owner, not the event's hosts, so being the owner is necessary but not sufficient.
 */
const FACT_RULES: Record<Action, FactRule> = {
  'organisation:view': 'ignore',
  'organisation:manage': 'ignore',
  'community:view': 'ignore',
  'community:manage': 'ignore',
  'community:setEntitlements': 'ignore',
  'natter:view': 'ignore',
  'natter:participate': 'ignore',
  'natter:manage': 'scopedOrIsOwned',
  'report:view': 'andIsOwned',
  'report:manage': 'andIsOwned',
};

type RuleOutcome = {
  granted: boolean;
  /**
   * Whether the sources were needed to reach `granted`. When they were not, the answer is already
   * determined, so it neither waits on any of them nor inherits their failure.
   */
  usedSources: boolean;
};

/**
 * Whether any source has actually said yes.
 *
 * Only a settled source can grant: one that is still loading has not answered, and one that could
 * not answer has nothing to say, so neither is read. It is an adapter's job to keep a
 * stale-but-usable answer out of `isError` if that is the behaviour it wants, because this will not
 * read one.
 */
const isGrantedBySomeSource = (sources: readonly PermissionsSource[], action: Action) =>
  sources.some((source) => !source.isLoading && !source.isError && source.allowed.has(action));

const applyRule = (
  rule: FactRule,
  action: Action,
  { sources, scope }: PermissionsSnapshot,
  facts: ResourceFacts | undefined
): RuleOutcome => {
  const grantedByAction = isGrantedBySomeSource(sources, action);
  // A permission held in one community must not leak into another, so the snapshot only speaks for
  // the community it was fetched for. Facts naming a different community - or naming none, which is
  // the documented way to ask for the narrow answer - take the snapshot out of play.
  const scopeCoversResource = facts === undefined || facts.communityId === scope?.communityId;

  switch (rule) {
    case 'ignore':
      return { granted: grantedByAction, usedSources: true };
    case 'scopedOrIsOwned':
      return facts?.isOwned
        ? { granted: true, usedSources: false }
        : { granted: scopeCoversResource && grantedByAction, usedSources: true };
    case 'andIsOwned':
      return facts !== undefined && !facts.isOwned
        ? { granted: false, usedSources: false }
        : { granted: grantedByAction, usedSources: true };
  }
};

/**
 * The one place that turns a snapshot plus what the caller knows about the resource into an answer.
 *
 * Pure by design - no React, no Redux, no network - so the whole permission model is readable and
 * testable in one file, which is what makes a change to who-can-do-what reviewable without reading
 * the call sites.
 *
 * Fails closed: an undetermined answer denies.
 *
 * Determinacy is a union across the sources that could contribute the action: a yes from any one of
 * them settles the question, and only a question nobody has answered yet waits or fails. The app has
 * one source today - permissions-service - so this is the general rule a second would inherit.
 */
export const evaluate = (snapshot: PermissionsSnapshot, action: Action, facts?: ResourceFacts): PermissionDecision => {
  const { granted, usedSources } = applyRule(FACT_RULES[action], action, snapshot, facts);

  // A grant is final whoever gave it: an outage elsewhere must not take away a capability that has
  // already been granted, which is what keeps staff authoring and the company admin's entitlements
  // working while the service is down.
  if (granted || !usedSources) return { allowed: granted, isLoading: false, isError: false };

  // Nobody said yes, so the answer is only undetermined if someone who COULD have said yes has yet
  // to speak. A source that does not contribute this action is irrelevant to it, however it is
  // doing - otherwise one slow source would hold every gate in the app.
  const isContributor = (source: PermissionsSource) => source.contributes.has(action);

  // Pending before unanswerable, matching `useCan`'s `combine`: a gate holds for an answer that may
  // still arrive rather than showing an outage message for it.
  if (snapshot.sources.some((source) => isContributor(source) && source.isLoading)) {
    return { allowed: false, isLoading: true, isError: false };
  }
  if (snapshot.sources.some((source) => isContributor(source) && source.isError)) {
    return { allowed: false, isLoading: false, isError: true };
  }

  return { allowed: false, isLoading: false, isError: false };
};
